import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  User, History, MapPin, LogOut, ShoppingBag, Star,
  Share2, Copy, Zap, Wallet, Gift, Sparkles, Camera, Info, Pencil, Trash2, X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PortalLoyaltyCard, getVipTier, TIER_BADGE_CLASS } from "./PortalLoyaltyCard";
import { TenantBlackLoyaltyCard } from "./TenantBlackLoyaltyCard";
import { PortalPrimeCard } from "./PortalPrimeCard";
import { PortalPrimeBanner } from "./PortalPrimeBanner";
import { PortalWeeklyMission } from "./PortalWeeklyMission";
import { PortalUrgencyBar } from "./PortalUrgencyBar";
import { PortalOrderCard } from "./PortalOrderCard";
import { PortalFinancialTab } from "./PortalFinancialTab";
import { availableCredit, formatCredit } from "@/utils/loyaltyCredit";

// Spain locale: the portal is served to a Spanish restaurant, so month
// abbreviations must be Spanish, not Portuguese.
const SHORT_MON = ['ene.','feb.','mar.','abr.','may.','jun.','jul.','ago.','sep.','oct.','nov.','dic.'];
function fmtSince(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return `${SHORT_MON[dt.getUTCMonth()]} ${dt.getUTCFullYear()}`;
}

export default function CustomerPortalDashboard({
  restaurantId, customer, orders, referralData, restaurant, primeStatus, primeOffers = [],
  onAvatarChange, onSaveAddress, onDeleteAddress, onLogout, onPrimeCancel,
}) {
  const navigate    = useNavigate();
  const [tab, setTab] = useState("prime");
  const [showRules, setShowRules] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const tenantName = restaurant?.name || "Restaurante";
  const savedAddresses = (customer?.addresses || []).filter(Boolean);

  // Prime is shown as active only when the backend confirms a paid subscription.
  const isPrime     = primeStatus?.is_prime === true;
  const totalOrders = customer?.total_orders    || 0;
  const totalSpent  = customer?.total_spent     || 0;
  const cashback    = customer?.cashback_balance || 0;
  // "Miembro desde" is the first purchase; no purchase yet => "Nuevo miembro".
  const memberSince = fmtSince(customer?.first_order_date);
  const tier        = getVipTier(totalSpent);
  const tierName    = customer?.vip_level || tier.name;
  const hasReferral = Boolean(referralData?.code);
  const loyaltyPoints = customer?.points ?? customer?.loyalty_points ?? 0;
  const credit      = availableCredit({ cashbackBalance: cashback, points: loyaltyPoints });

  const tabCols = hasReferral ? "grid-cols-5" : "grid-cols-4";

  const handleReorder = (order) => {
    sessionStorage.setItem(`kaiso_reorder_${restaurantId}`, JSON.stringify(order.items || []));
    navigate(`/m/${restaurantId}`);
  };

  const handleTrack = (id) => navigate(`/track/${id}`);

  const handleCopy = () => {
    if (!referralData?.code) return;
    navigator.clipboard.writeText(referralData.code)
      .then(() => import("sonner").then(({ toast }) => toast.success("¡Código copiado!")));
  };

  const handleShare = () => {
    if (!referralData?.code) return;
    const text = `Usa mi código ${referralData.code} y consigue un ${referralData.discount_percent || 10}% de descuento. 🎉`;
    if (navigator.share) navigator.share({ text });
    else navigator.clipboard.writeText(text)
      .then(() => import("sonner").then(({ toast }) => toast.success("¡Mensaje copiado!")));
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="mx-auto grid max-w-6xl gap-4 px-3 py-3 sm:px-5 md:grid-cols-[220px_minmax(0,1fr)] md:gap-5 md:py-5">
        <aside className="hidden rounded-[28px] border border-white/10 bg-[#0b0b0d] p-4 shadow-2xl md:flex md:min-h-[calc(100vh-40px)] md:flex-col md:justify-between">
          <div>
            <div className="mb-8 flex items-center gap-3 px-2">
              <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl border border-[#c9a227]/60 bg-black shadow-[0_0_24px_rgba(197,156,62,.25)]">
                {restaurant?.logo_url ? <img src={restaurant.logo_url} alt={tenantName} className="h-full w-full object-contain p-1" /> : <Sparkles className="h-5 w-5 text-[#f4d66d]" />}
              </div>
              <div className="min-w-0"><p className="truncate text-xs font-black uppercase tracking-[.16em] text-white">{tenantName}</p><p className="text-[10px] text-white/45">Portal del cliente</p></div>
            </div>
            <nav className="space-y-2" aria-label="Navegação do portal">
              {[["orders", History, "Pedidos"], ["prime", Zap, "Prime"], ["financial", Wallet, "Finanzas"], ["profile", User, "Perfil"]].map(([value, Icon, label]) => (
                <button key={value} type="button" onClick={() => setTab(value)} className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition ${tab === value ? "bg-[#c9a227]/15 text-[#f4d66d] shadow-[inset_0_0_0_1px_rgba(201,162,39,.45)]" : "text-white/55 hover:bg-white/5 hover:text-white"}`}><Icon className="h-4 w-4" />{label}</button>
              ))}
            </nav>
          </div>
          <div className="border-t border-white/10 pt-4"><p className="truncate text-xs font-bold text-white">{customer?.name || "Cliente"}</p><p className="mt-1 text-[10px] text-white/40">{tierName} · {loyaltyPoints} pontos</p></div>
        </aside>
        <main className="min-w-0 rounded-[30px] bg-[#f8f8f8] text-gray-900 shadow-2xl md:p-3">
          <div className="mx-auto max-w-2xl px-4 py-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`relative w-13 h-13 w-12 h-12 rounded-2xl bg-gradient-to-br ${tier.from} ${tier.to} flex items-center justify-center shrink-0 shadow-md overflow-hidden`}>
              {customer?.avatar_url ? <img src={customer.avatar_url} alt="" className="w-full h-full object-cover" /> : <User className="w-6 h-6 text-white drop-shadow" />}
              <label className="absolute bottom-0 right-0 bg-black/60 rounded-tl-lg p-1 cursor-pointer" title="Cambiar foto">
                <Camera className="w-3 h-3 text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={e => onAvatarChange?.(e.target.files?.[0])} />
              </label>
            </div>
            <div>
              <h1 className="font-black text-lg leading-tight text-gray-900">{customer?.name || "Cliente"}</h1>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <Badge className={`${TIER_BADGE_CLASS[tierName] || TIER_BADGE_CLASS.Bronze} border text-[10px] font-bold px-1.5 py-0`}>
                  {tierName}
                </Badge>
                {isPrime && (
                  <Badge className="bg-violet-600 text-white border-0 text-[10px] font-bold px-1.5 py-0 gap-0.5">
                    <Zap className="w-2.5 h-2.5" />Prime
                  </Badge>
                )}
                {memberSince && (
                  <span className="text-[11px] text-gray-400">desde {memberSince}</span>
                )}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout} className="text-gray-400 hover:text-gray-600 p-2">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>

        {/* Urgency alerts */}
        <PortalUrgencyBar customer={customer} />

        <TenantBlackLoyaltyCard
          restaurantId={restaurantId}
          restaurant={restaurant}
          loyaltyPoints={loyaltyPoints}
          credit={credit}
          onShowRules={() => setShowRules(true)}
          onUseNow={() => navigate("/m/" + restaurantId + "?use_loyalty=1")}
        />

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center p-3 bg-white rounded-xl border shadow-sm">
            <p className="text-xl font-black text-gray-900">{totalOrders}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Pedidos</p>
          </div>
          <div className={`text-center p-3 rounded-xl border shadow-sm bg-gradient-to-br ${tier.from} ${tier.to}`}>
            <p className="text-xl font-black text-white drop-shadow-sm">{tierName}</p>
            <p className="text-[11px] text-white/70 mt-0.5">Nivel VIP</p>
          </div>
          <div className="text-center p-3 bg-white rounded-xl border shadow-sm">
            <p className="text-base font-black text-gray-700 leading-tight">{memberSince || "Nuevo miembro"}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Miembro desde</p>
          </div>
        </div>

        {/* Weekly Mission */}
        <PortalWeeklyMission orders={orders} />

        {/* Main Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className={`w-full grid bg-gray-100 rounded-xl p-1 ${tabCols}`}>
            <TabsTrigger value="orders" className="rounded-lg text-xs gap-1">
              <History className="w-3.5 h-3.5" />Pedidos
            </TabsTrigger>
            <TabsTrigger value="prime" className="rounded-lg text-xs gap-0.5 font-black text-violet-700 bg-violet-50 data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-700 data-[state=active]:to-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:[&_svg]:text-white">
              <Zap className={`w-3.5 h-3.5 ${isPrime ? "text-yellow-500" : ""}`} />Prime
            </TabsTrigger>
            <TabsTrigger value="financial" className="rounded-lg text-xs gap-1">
              <Wallet className="w-3.5 h-3.5" />Finanzas
            </TabsTrigger>
            {hasReferral && (
              <TabsTrigger value="referral" className="rounded-lg text-xs gap-1">
                <Share2 className="w-3.5 h-3.5" />Referral
              </TabsTrigger>
            )}
            <TabsTrigger value="profile" className="rounded-lg text-xs gap-1">
              <User className="w-3.5 h-3.5" />Perfil
            </TabsTrigger>
          </TabsList>

          {/* Pedidos */}
          <TabsContent value="orders" className="mt-4 space-y-3">
            {orders.length === 0 ? (
              <div className="text-center py-14">
                <ShoppingBag className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Todavía no has hecho ningún pedido</p>
                <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={() => navigate(`/m/${restaurantId}`)}>
                  <ShoppingBag className="w-4 h-4" />Ver carta
                </Button>
              </div>
            ) : orders.map(order => (
              <PortalOrderCard
                key={order.id}
                order={order}
                onReorder={handleReorder}
                onTrack={handleTrack}
                showFinancial={false}
              />
            ))}
          </TabsContent>

          {/* Prime */}
          <TabsContent value="prime" className="mt-4">
            {isPrime
              ? <PortalPrimeCard
                  restaurantId={restaurantId}
                  subscription={primeStatus?.subscription}
                  coupon={primeStatus?.coupon}
                  offers={primeOffers}
                  onCancel={onPrimeCancel}
                />
              : <PortalPrimeBanner
                  restaurantId={restaurantId}
                  phone={primeStatus?.phone || ""}
                  customerName={customer?.name || ""}
                  gateways={primeStatus?.gateways || []}
                  planPrice={primeStatus?.plan_price ?? 3.99}
                />
            }
          </TabsContent>

          {/* Financeira */}
          <TabsContent value="financial" className="mt-4">
            <PortalFinancialTab customer={customer} orders={orders} />
          </TabsContent>

          {/* Referral */}
          {hasReferral && (
            <TabsContent value="referral" className="mt-4 space-y-4">
              <div className="rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-600 text-white p-6 text-center shadow-lg">
                <Share2 className="w-8 h-8 mx-auto mb-3 text-purple-200" />
                <h3 className="font-black text-xl mb-1">Invita a un amigo</h3>
                <p className="text-purple-100 text-sm mb-4">
                  Tú y tu amigo conseguís <strong>{referralData.discount_percent || 10}%</strong> de descuento en el próximo pedido
                </p>
                <div className="bg-white/20 rounded-xl p-3 mb-4">
                  <p className="font-mono font-black text-2xl tracking-widest">{referralData.code}</p>
                </div>
                <div className="flex gap-2 justify-center">
                  <Button variant="secondary" size="sm" className="gap-1.5" onClick={handleCopy}>
                    <Copy className="w-3.5 h-3.5" />Copiar
                  </Button>
                  <Button size="sm" className="bg-white text-purple-700 hover:bg-purple-50 gap-1.5" onClick={handleShare}>
                    <Share2 className="w-3.5 h-3.5" />Compartir
                  </Button>
                </div>
                {referralData.referral_count > 0 && (
                  <p className="mt-3 text-purple-200 text-xs">
                    {referralData.referral_count} amigo{referralData.referral_count !== 1 ? "s" : ""} invitado{referralData.referral_count !== 1 ? "s" : ""} ✓
                  </p>
                )}
              </div>
              <Card>
                <CardContent className="p-4">
                  <p className="font-semibold text-sm text-gray-700 mb-2">¿Cómo funciona?</p>
                  <ol className="list-decimal list-inside space-y-1 text-xs text-gray-500">
                    <li>Comparte tu código con un amigo</li>
                    <li>Tu amigo usa el código en su primer pedido</li>
                    <li>Los dos recibís un {referralData.discount_percent || 10}% de descuento</li>
                  </ol>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Perfil */}
          <TabsContent value="profile" className="mt-4 space-y-3">
            <Card className="border-[#c9a227]/25"><CardContent className="p-4 flex items-center gap-3"><div className="w-14 h-14 rounded-2xl overflow-hidden bg-[#101010] flex items-center justify-center border border-[#c9a227]/40">{customer?.avatar_url ? <img src={customer.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="font-black text-[#f4d66d] text-xl">{(customer?.name || "C").slice(0, 1).toUpperCase()}</span>}</div><div><p className="font-black text-gray-900">Foto de perfil</p><p className="text-xs text-gray-500">Aparece en tu perfil y en los pedidos de cocina.</p><label className="inline-flex mt-2 items-center gap-1 text-xs font-bold text-[#a67c00] cursor-pointer"><Camera className="w-3.5 h-3.5" />Cambiar foto<input type="file" accept="image/*" className="hidden" onChange={e => onAvatarChange?.(e.target.files?.[0])} /></label></div></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><Star className="w-4 h-4 text-[#c9a227]" />Favoritos</p><p className="text-xs text-gray-500">{customer?.favorites?.length ? `${customer.favorites.length} productos guardados` : "Todavía no tienes favoritos — ¡explora la carta!"}</p></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center justify-between mb-3"><p className="text-sm font-black text-gray-800 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#c62828]" />Direcciones guardadas</p><Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditingAddress({street:"", complement:"", city:"", postal_code:"", is_default:!(customer?.addresses?.length)})}><span className="mr-1">+</span>Añadir</Button></div>{savedAddresses.length ? <div className="space-y-2">{savedAddresses.map((addr,i)=><div key={addr.id||i} className="flex items-center justify-between gap-2 p-3 bg-gray-50 rounded-xl"><div><p className="text-sm font-medium text-gray-700">{addr.street}</p><p className="text-xs text-gray-500">{[addr.complement,addr.city,addr.postal_code].filter(Boolean).join(" · ")}</p>{addr.is_default&&<Badge className="mt-1 text-xs bg-[#fff3c4] text-[#8a6500]">Predeterminada</Badge>}</div><div className="flex gap-1"><Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEditingAddress(addr)}><Pencil className="w-3.5 h-3.5"/></Button><Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => onDeleteAddress?.(addr.id)}><Trash2 className="w-3.5 h-3.5"/></Button></div></div>)}</div> : <p className="text-xs text-gray-400">No tienes direcciones guardadas</p>}</CardContent></Card>
          </TabsContent>
        </Tabs>

        {/* CTA */}
        <Button
          className="w-full bg-[#c62828] hover:bg-[#a91f1f] active:scale-[0.98] h-12 text-base font-black gap-2 rounded-2xl shadow-xl transition-transform"
          onClick={() => navigate(`/m/${restaurantId}`)}
        >
          <ShoppingBag className="w-5 h-5" />Hacer nuevo pedido →
        </Button>
          </div>
        </main>
      </div>
 <AddressEditor address={editingAddress} onClose={() => setEditingAddress(null)} onSave={(a) => { onSaveAddress?.(a); setEditingAddress(null); }} />}
      {showRules && <div className="fixed inset-0 z-50 bg-black/60 p-4 flex items-center justify-center" onClick={() => setShowRules(false)}><div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}><div className="flex justify-between items-center"><h2 className="text-xl font-black text-gray-900">Reglas del crédito</h2><Button variant="ghost" size="sm" onClick={() => setShowRules(false)}><X className="w-4 h-4"/></Button></div><div className="mt-4 space-y-3 text-sm text-gray-600"><p>• Cada 100€ pagados generan 10€ de crédito.</p><p>• El crédito puede usarse en productos elegibles del menú.</p><p>• Los productos en oferta no acumulan descuento de fidelidad.</p><p>• Los días con un 10% de promoción en la web, los descuentos no se acumulan.</p><p>• Los miércoles no se puede usar crédito en pedidos de mesa.</p></div><Button onClick={() => setShowRules(false)} className="mt-5 w-full bg-[#101010] text-[#f4d66d] rounded-xl">Entendido</Button></div></div>}
    </div>
  );
}

function AddressEditor({ address, onClose, onSave }) {
  const [draft, setDraft] = useState(() => address || {});
  if (!address) return null;
  const field = (key, label) => <input value={draft[key] || ""} onChange={e => setDraft({...draft,[key]:e.target.value})} placeholder={label} className="w-full border rounded-xl px-3 py-2 text-sm" />;
  return <div className="fixed inset-0 z-50 bg-black/60 p-4 flex items-center justify-center"><div className="bg-white rounded-3xl max-w-md w-full p-5 shadow-2xl space-y-3"><div className="flex justify-between"><h2 className="font-black text-lg">Editar dirección</h2><Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4"/></Button></div>{field("street","Calle y número")}{field("complement","Complemento")}{field("city","Ciudad")}{field("postal_code","Código postal")}<label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!draft.is_default} onChange={e=>setDraft({...draft,is_default:e.target.checked})}/> Usar como predeterminada</label><Button className="w-full bg-[#c62828] rounded-xl" onClick={() => onSave(draft)}>Guardar dirección</Button></div></div>;
}

